var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Utils, CustomElement, Watch, P, PropertyConverters, Debounce } from '@pacem/pacem-core';
import { LatLngBounds } from './types';
import { PacemMapAdapterElement, PacemOverlayAdapter, PacemLayerAdapter, PacemMarkerAdapter, PacemShapeAdapter, PacemMapPolylineElement, PacemMapPolygonElement, PacemMapCircleElement, PacemMapOverlayImageElement, MapUtils, MapColorScheme, MapType, MapEvent } from './types-components';
// namespace Pacem.Components.Maps {
const consts = {
    DETACH_TIMEOUT: 200,
    /**@deprecated */
    API_URI: 'https://maps.googleapis.com/maps/api/js',
    MAX_ROADMAP_ZOOM: 22,
    //MAX_SATELLITE_ZOOM: 22,
    BOOTSTRAP_SNIPPET: (key, lang, region, v) => {
        return `(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.$\{c\}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
            key: "${key}", v: "${v}", language: "${lang}", region: "${region}",
    // Add other bootstrap parameters as needed, using camel case.
  });`;
    },
    MAPS_NOT_FOUND: 'google.maps not found.',
    GEOCODER_UNAVAILABLE: 'google.maps geocoder unavailable.',
    INVALID_KEY: 'invalid google.maps api key.',
    TILE_EXTENT: [-Math.PI * 6378137, Math.PI * 6378137],
};
class Bootstrapper {
    #promise;
    #params;
    _equivalent(key, language, region, version) {
        const guard = this.#params;
        return !Utils.isNull(guard)
            && guard.key === key
            // fallback on curreny values when not explicitated
            && guard.language === (language || guard.language)
            && guard.region === (region || guard.region)
            && guard.version === (version || guard.version);
    }
    _spinFuture(key, language, region, version) {
        return new Promise((resolve, reject) => {
            // check if already bootstrapped with the same variables
            if (!this._equivalent(key, language, region, version)) {
                Bootstrapper._ensureBootstrapScript(key, language, region, version);
            }
            this.#params = { language, region, version, key };
            google.maps.importLibrary("maps").then(_ => {
                resolve();
                this.#promise = null;
            }, e => {
                reject(e);
                this.#promise = null;
            });
        });
    }
    boostrap(key, language, region, version = 'weekly') {
        if (Utils.isNullOrEmpty(key)) {
            return Promise.reject(consts.INVALID_KEY);
        }
        if (Utils.isNull(this.#promise) || !this._equivalent(key, language, region, version)) {
            this.#promise = this._spinFuture(key, language, region, version);
        }
        return this.#promise;
    }
    static _ensureBootstrapScript(key, language, region, v) {
        const uniqueAttrName = 'pacem-googlemaps';
        const uniqueAttrValue = `v=${v},l=${language},r=${region}`;
        let script = document.querySelector(`script[${uniqueAttrName}]`);
        if (!Utils.isNull(script)) {
            if (script.getAttribute(uniqueAttrName) === uniqueAttrValue) {
                return;
            }
            else {
                script.remove();
                document.querySelectorAll('script[src^=\'https://maps.googleapis.com/maps\']').forEach(i => i.remove());
                delete window['google'];
            }
        }
        if (Utils.isNullOrEmpty(key)) {
            return;
        }
        script = document.createElement('script');
        script.setAttribute(uniqueAttrName, uniqueAttrValue);
        script.textContent = consts.BOOTSTRAP_SNIPPET(key, language, region, v);
        document.head.appendChild(script);
    }
}
/** Bootstrapper singleton. */
const bootstrapper = new Bootstrapper();
class GMapsUtils {
    static getPosition(latLng) {
        if (Array.isArray(latLng)) {
            return { lat: latLng[0], lng: latLng[1] };
        }
        return latLng;
    }
    static getBounds(bnds) {
        if (Utils.isNullOrEmpty(bnds)) {
            return null;
        }
        return { north: bnds.ne.lat, east: bnds.ne.lng, south: bnds.sw.lat, west: bnds.sw.lng };
    }
    static mapGeocodeResults(a, b = []) {
        const retval = [];
        a ??= [];
        for (let j = 0; j < a.length; j++) {
            const elevation = b.length > j ? b[j] : null;
            const address = GMapsUtils.mapGeocodeResult(a[j], elevation);
            retval.push(address);
        }
        return retval;
    }
    static mapGeocodeResult(a, b) {
        const address = a?.address_components || [];
        const buildingNumber = address.find(a => a.types.indexOf('street_number') >= 0)?.long_name;
        const streetName = address.find(a => a.types.indexOf('route') >= 0)?.long_name;
        const city = address.find(a => a.types.indexOf('locality') >= 0)?.long_name;
        const comune = address.find(a => a.types.indexOf("administrative_area_level_3") >= 0)?.long_name;
        const provinceObj = address.find(a => a.types.indexOf("administrative_area_level_2") >= 0);
        const region = address.find(a => a.types.indexOf("administrative_area_level_1") >= 0)?.short_name;
        const countryObj = address.find(a => a.types.indexOf("country") >= 0);
        const zipCode = address.find(a => a.types.indexOf("postal_code") >= 0)?.short_name;
        const structuredAddress = {
            houseNumber: buildingNumber,
            city: comune || city,
            provinceCode: provinceObj?.short_name,
            province: provinceObj?.long_name,
            countryCode: countryObj?.short_name,
            country: countryObj?.long_name,
            zipCode,
            streetName,
            state: region,
            village: city,
            type: a.geometry.location_type
        };
        const retval = {
            fullAddress: a.formatted_address,
            position: GMapsUtils.toLiteral(a.geometry.location, b?.elevation),
            address: structuredAddress
        };
        return retval;
    }
    static toLiteral(l, e) {
        if (Utils.isNull(l)) {
            return null;
        }
        if ('lat' in l && typeof l.lat === 'function' && 'lng' in l && typeof l.lng === 'function') {
            return { lat: l.lat(), lng: l.lng(), altitude: e };
        }
        if ('getNorthEast' in l && typeof l.getNorthEast === 'function' && 'getSouthWest' in l && typeof l.getSouthWest === 'function') {
            const ne = l.getNorthEast(), sw = l.getSouthWest();
            return { ne: GMapsUtils.toLiteral(ne), sw: GMapsUtils.toLiteral(sw) };
        }
        if ('north' in l && typeof l.north === 'number'
            && 'south' in l && typeof l.south === 'number'
            && 'west' in l && typeof l.west === 'number'
            && 'east' in l && typeof l.east === 'number') {
            return {
                ne: { lat: l.north, lng: l.east }, sw: { lat: l.south, lng: l.west }
            };
        }
        return Utils.extend({ altitude: e }, l);
    }
    static areEqual(l1, l2) {
        if (Utils.isNull(l1) || Utils.isNull(l2)) {
            return false;
        }
        const latLng1 = GMapsUtils.toLiteral(l1), latLng2 = GMapsUtils.toLiteral(l2);
        return latLng1.lat === latLng2.lat && latLng1.lng === latLng2.lng;
    }
    static fitBounds(map, bnds, padding) {
        //map.panToBounds(bnds, padding);
        map?.fitBounds(bnds, padding);
    }
    static setCenter(map, center) {
        if (!Utils.isNull(map) && !GMapsUtils.areEqual(map.getCenter(), center)) {
            map.panTo(center);
            //map.setCenter(center);
        }
    }
}
class PacemGoogleShapeAdapter extends PacemShapeAdapter {
    getShapeBounds(shape) {
        const bnds = new google.maps.LatLngBounds();
        if (shape instanceof google.maps.Circle) {
            const b = shape.getBounds();
            if (b) {
                bnds.extend(b.getNorthEast());
                bnds.extend(b.getSouthWest());
            }
        }
        else if (shape instanceof google.maps.Polygon) {
            shape.getPath().forEach(l => bnds.extend(l));
        }
        else if (shape instanceof google.maps.Polyline) {
            shape.getPath().forEach(l => bnds.extend(l));
        }
        if (bnds.isEmpty() !== false) {
            return LatLngBounds.Empty;
        }
        return GMapsUtils.toLiteral(bnds);
    }
    deleteShape(shape, map) {
        shape.setMap(null);
    }
    getShapePosition(obj) {
        if ('latLng' in obj) {
            return obj.latLng;
        }
        return null;
    }
    buildShape(item, map) {
        const clickable = true;
        if (item instanceof PacemMapPolylineElement) {
            return new google.maps.Polyline({
                path: item.vertices,
                clickable,
                map
            });
        }
        if (item instanceof PacemMapPolygonElement) {
            return new google.maps.Polygon({
                paths: [item.vertices],
                clickable,
                map
            });
        }
        if (item instanceof PacemMapCircleElement) {
            return new google.maps.Circle({
                center: item.center,
                radius: item.radius,
                clickable,
                map
            });
        }
        return null;
    }
    addClickEventListener(shape, callback) {
        shape.addListener('click', callback);
    }
    addDragEventListener(shape, callback) {
        shape.addListener('drag', callback);
    }
    addDragStartEventListener(shape, callback) {
        shape.addListener('dragstart', callback);
    }
    addDragEndEventListener(shape, callback) {
        shape.addListener('dragend', callback);
    }
    setShape(shape, item) {
        if (item instanceof PacemMapPolylineElement) {
            const polyline = shape;
            polyline.setOptions({
                strokeColor: item.stroke?.color,
                strokeOpacity: item.stroke?.alpha ?? 1,
                strokeWeight: item.stroke?.thickness,
                path: item.vertices,
            });
        }
        if (item instanceof PacemMapPolygonElement) {
            const polygon = shape;
            polygon.setOptions({
                strokeColor: item.stroke?.color,
                strokeOpacity: item.stroke?.alpha ?? 1,
                strokeWeight: item.stroke?.thickness,
                strokePosition: google.maps.StrokePosition.CENTER,
                fillColor: item.fill?.color,
                fillOpacity: item.fill?.alpha,
                paths: [item.vertices]
            });
        }
        if (item instanceof PacemMapCircleElement) {
            const circle = shape;
            circle.setOptions({
                strokeColor: item.stroke?.color,
                strokeOpacity: item.stroke?.alpha ?? 1,
                strokeWeight: item.stroke?.thickness,
                strokePosition: google.maps.StrokePosition.CENTER,
                fillColor: item.fill?.color,
                fillOpacity: item.fill?.alpha,
                center: item.center,
                radius: item.radius
            });
        }
    }
    setEditable(shape, editable) {
        shape.setOptions({
            editable
        });
    }
    setDraggable(shape, draggable) {
        shape.setOptions({
            draggable
        });
    }
}
class PacemGoogleMarkerAdapter extends PacemMarkerAdapter {
    deleteInfoWindow(info, map) {
        info.unbindAll();
    }
    deleteMarker(marker, map) {
        marker.setMap(null);
    }
    get mapAutomaticallyScrollsOnDrag() {
        return true;
    }
    getMarkerPosition(marker) {
        const latLng = marker.getPosition();
        return { lat: latLng.lat(), lng: latLng.lng() };
    }
    buildMarker(item, map) {
        return new google.maps.Marker({
            position: item.position,
            map
        });
    }
    addClickEventListener(marker, map, callback) {
        marker.addListener('click', callback);
    }
    addDragEventListener(marker, map, callback) {
        marker.addListener('drag', callback);
    }
    addDragEndEventListener(marker, map, callback) {
        marker.addListener('dragend', callback);
    }
    setPosition(marker, position) {
        marker.setPosition(position);
    }
    setIcon(marker, icon) {
        if (typeof icon === 'string') {
            // icon url only
            marker.setIcon(icon);
        }
        else if (!Utils.isNull(icon)) {
            // structured icon
            let options = { url: icon.url };
            if (!Utils.isNullOrEmpty(icon.size)) {
                options.size = new google.maps.Size(icon.size.width, icon.size.height);
                options.anchor = new google.maps.Point(icon.size.width / 2, icon.size.height);
            }
            if (!Utils.isNullOrEmpty(icon.anchor)) {
                options.anchor = new google.maps.Point(icon.anchor.x, icon.anchor.y);
            }
            marker.setIcon(options);
        }
    }
    setLabel(marker, label) {
        marker.setLabel(label);
    }
    setDraggable(marker, draggable) {
        marker.setDraggable(draggable);
    }
    closeInfo(info) {
        info.close();
    }
    openInfo(info, _, map, marker) {
        info.open(map, marker);
    }
    buildInfoWindow(item) {
        let offsX = 0;
        if (typeof item.icon !== 'string' && !Utils.isNull(item.icon)) {
            offsX = ((item.icon.size?.width ?? 0) / 2) -
                (item.icon.anchor?.x ?? 0);
        }
        return new google.maps.InfoWindow({
            pixelOffset: new google.maps.Size(-offsX, 0)
        });
    }
    addCloseClickEventListener(info, map, callback) {
        info.addListener('closeclick', callback);
    }
    setContent(info, content) {
        info.setContent(content);
    }
}
class PacemGoogleLayerAdapter extends PacemLayerAdapter {
    addLoadEventListener(layer, map, callback) {
        layer.addListener('tilesloaded', callback);
    }
    removeLoadEventListener(layer, map, callback) {
        // not necessary
    }
    deleteLayer(layer, map) {
        const layers = map.overlayMapTypes.getArray();
        const index = layers.findIndex(v => v === layer);
        if (index >= 0) {
            map.overlayMapTypes.removeAt(index);
        }
    }
    _getBounds(x, y, zoom) {
        const ext = consts.TILE_EXTENT;
        var tileSize = (ext[1] * 2) / Math.pow(2, zoom);
        var minx = ext[0] + x * tileSize;
        var maxx = ext[0] + (x + 1) * tileSize;
        // remember y origin starts at top
        var miny = ext[1] - (y + 1) * tileSize;
        var maxy = ext[1] - y * tileSize;
        return [minx, miny, maxx, maxy];
    }
    _getWmsTileUrl(url, layers) {
        return (coords, zoom) => {
            const tileUrl = Utils.URIs.appendQuery(url, {
                service: 'WMS', styles: '', transparent: 'true',
                request: 'GetMap', srs: 'EPSG:3857', format: 'image/png', version: '1.1.1',
                layers: layers?.join(',') || '', width: '256', height: '256',
                bbox: this._getBounds(coords.x, coords.y, zoom).join(',')
            });
            return tileUrl;
        };
    }
    _getWmsLayer(item, map) {
        const wmsLayer = new google.maps.ImageMapType({
            getTileUrl: this._getWmsTileUrl(item.url, item.include),
            //name: item.id,
            //alt: "TODO",
            minZoom: item.minZoom,
            maxZoom: item.maxZoom,
            opacity: 1 /* TODO */,
        });
        map.overlayMapTypes.push(wmsLayer);
        return wmsLayer;
    }
    _getTmsTileUrl(template) {
        const subdomains = 'abc';
        function getSubDomain(tilePoint) {
            const index = Math.abs(tilePoint.x + tilePoint.y) % subdomains.length;
            return subdomains[index];
        }
        //{s}.tile.openstreetmap.org/{z}/{x}/{y}.png
        return (coords, zoom) => {
            const data = {
                r: /*retina ? '@2x' :*/ '',
                s: getSubDomain(coords),
                x: '' + coords.x,
                y: '' + coords.y,
                z: '' + zoom
            };
            let tileUrl = template;
            for (let prop in data) {
                tileUrl = tileUrl.replaceAll('{' + prop + '}', data[prop]);
            }
            return tileUrl;
        };
    }
    _getTmsLayer(item, map) {
        const wmsLayer = new google.maps.ImageMapType({
            getTileUrl: this._getTmsTileUrl(item.url),
            //name: item.id,
            //alt: "TODO",
            minZoom: item.minZoom,
            maxZoom: item.maxZoom,
            opacity: 1 /* TODO */,
        });
        map.overlayMapTypes.push(wmsLayer);
        return wmsLayer;
    }
    buildLayer(item, map) {
        const mode = (item.mode || '').toLowerCase();
        switch (mode) {
            case 'wms':
                return this._getWmsLayer(item, map);
            case 'tms':
                return this._getTmsLayer(item, map);
            default:
                throw new Error(`"${item.mode}" overlays not supported for the Google Maps integration.`);
        }
    }
}
function createOverlay(item) {
    //#region closure classes
    class PacemGoogleOverlay extends google.maps.OverlayView {
        constructor(_item) {
            super();
            this._item = _item;
        }
        get element() {
            return this._item;
        }
        get bounds() {
            const bounds = this._item.bounds;
            if (Utils.isNull(bounds)) {
                return null;
            }
            return GMapsUtils.getBounds(bounds);
        }
        getBoundingBox() {
            const proj = this.getProjection(), bounds = this.bounds;
            if (Utils.isNullOrEmpty(bounds)) {
                return null;
            }
            const sw = proj.fromLatLngToDivPixel({ lat: bounds.south, lng: bounds.west });
            const ne = proj.fromLatLngToDivPixel({ lat: bounds.north, lng: bounds.east });
            return { x: sw.x, y: ne.y, width: ne.x - sw.x, height: sw.y - ne.y };
        }
    }
    class PacemGoogleImageOverlay extends PacemGoogleOverlay {
        get src() {
            return this.element.src;
        }
        #div;
        onAdd() {
            // TODO: switch to canvas
            const div = this.#div = this.#div || document.createElement('div');
            div.style.position = 'absolute';
            const panes = this.getPanes();
            panes.overlayLayer.appendChild(div);
        }
        onRemove() {
            const div = this.#div;
            if (!Utils.isNull(div)) {
                div.remove();
                this.#div = null;
            }
        }
        draw() {
            const div = this.#div;
            if (Utils.isNull(div)) {
                return;
            }
            const bbox = this.getBoundingBox();
            if (Utils.isNull(bbox)) {
                return;
            }
            div.style.top = bbox.y + 'px';
            div.style.left = bbox.x + 'px';
            div.style.width = bbox.width + 'px';
            div.style.height = bbox.height + 'px';
            div.style.backgroundSize = `${bbox.width}px ${bbox.height}px`;
            div.style.backgroundImage = `url(${this.src})`;
        }
    }
    //#endregion
    if (item instanceof PacemMapOverlayImageElement) {
        return new PacemGoogleImageOverlay(item);
    }
    return null;
}
;
class PacemGoogleOverlayAdapter extends PacemOverlayAdapter {
    updateOverlay(overlay, map, item) {
        overlay.set;
        overlay.draw();
    }
    deleteOverlay(overlay, map) {
        overlay.setMap(null);
    }
    buildOverlay(item, map) {
        let overlay = createOverlay(item);
        overlay.setMap(map);
        return overlay;
    }
    setBounds(item, map, bounds) {
        item.draw();
    }
}
const MAP_EVENTS = ['dragend', 'zoom_changed', 'center_changed', 'maptypeid_changed', 'tilt_changed', 'heading_changed', 'bounds_changed'];
function getNativeMapStyle(options) {
    const theme = options.theme == MapColorScheme.DARK ? google.maps.ColorScheme.DARK : (options.theme != MapColorScheme.LIGHT ? google.maps.ColorScheme.FOLLOW_SYSTEM : google.maps.ColorScheme.LIGHT);
    switch (options.type) {
        case google.maps.MapTypeId.HYBRID:
        case google.maps.MapTypeId.ROADMAP:
        case google.maps.MapTypeId.TERRAIN:
        case google.maps.MapTypeId.SATELLITE:
            return { type: options.type, theme };
        case MapType.HYBRID:
            return { type: google.maps.MapTypeId.HYBRID, theme };
        case MapType.SATELLITE:
            return { type: google.maps.MapTypeId.SATELLITE, theme };
        case MapType.TERRAIN:
            return { type: google.maps.MapTypeId.TERRAIN, theme };
        default:
            return { type: google.maps.MapTypeId.ROADMAP, theme };
    }
}
let PacemGoogleMapAdapterElement = class PacemGoogleMapAdapterElement extends PacemMapAdapterElement {
    geocode(address, maxResults) {
        const language = this.language || '', region = this.region || '', version = this.version || 'weekly';
        const geocoder = new PacemGoogleMapGeocoder(this.apiKey, language, region, version);
        return geocoder.geocode(address, maxResults);
    }
    reverseGeocode(latLng, maxResults) {
        const language = this.language || '', region = this.region || '', version = this.version || 'weekly';
        const geocoder = new PacemGoogleMapGeocoder(this.apiKey, language, region, version);
        return geocoder.reverseGeocode(latLng, maxResults);
    }
    getBounds(map) {
        if (!this.hasMap(map)) {
            return LatLngBounds.Empty;
        }
        const nativeMap = this.getMap(map);
        const retval = nativeMap.getBounds();
        return GMapsUtils.toLiteral(retval);
    }
    isActive() {
        return !Utils.isNullOrEmpty(this.apiKey);
    }
    buildMarkerAdapterFor(map) {
        return new PacemGoogleMarkerAdapter(map, this);
    }
    buildOverlayAdapterFor(map) {
        return new PacemGoogleOverlayAdapter(map, this);
    }
    buildShapeAdapterFor(map) {
        return new PacemGoogleShapeAdapter(map, this);
    }
    buildLayerAdapterFor(map) {
        return new PacemGoogleLayerAdapter(map, this);
    }
    getMaxAvailableZoom(latLng) {
        return new Promise((resolve, reject) => {
            const svc = new google.maps.MaxZoomService();
            svc.getMaxZoomAtLatLng(latLng, result => {
                if (result.status === google.maps.MaxZoomStatus.OK) {
                    resolve(result.zoom);
                }
                else {
                    reject();
                }
            });
        });
    }
    #listeners = new WeakMap();
    snapshot(ctrl, type = 'image/png', quality) {
        const { container } = this.tryGetMap(ctrl);
        return Utils.snapshotElement(container, null, type, quality);
    }
    setViewNative(ctrl, options) {
        const map = this.getMap(ctrl);
        options = Utils.extend({ type: ctrl.type, theme: ctrl.theme }, options);
        const { type, theme } = getNativeMapStyle(options);
        if (this.#bootstrapped && theme !== map.get('colorScheme')) {
            this._bootstrapDebounced();
            return;
        }
        map.setOptions({ colorScheme: theme, mapTypeId: type });
        if (typeof options.tilt === 'number') {
            map.setTilt(options.tilt);
        }
        if (typeof options.heading === 'number') {
            map.setHeading(options.heading);
        }
        // `setZoom` BEFORE
        // calling `panTo` method, otherwise
        // no smooth transition will be applied.
        const fnSetCenter = () => {
            if (options.center) {
                GMapsUtils.setCenter(map, options.center);
            }
        };
        if (typeof options.zoom === 'number' && options.zoom !== map.getZoom()) {
            const mapType = map.getMapTypeId();
            switch (mapType) {
                case google.maps.MapTypeId.SATELLITE:
                case google.maps.MapTypeId.HYBRID:
                    // async retrieve
                    const currentCenter = GMapsUtils.toLiteral(map.getCenter());
                    this.getMaxAvailableZoom(currentCenter).then(z => {
                        const zoom = Math.min(z, options.zoom);
                        map.setZoom(zoom);
                        fnSetCenter();
                    });
                    break;
                default:
                    const zoom = Math.min(consts.MAX_ROADMAP_ZOOM, options.zoom);
                    map.setZoom(zoom);
                    fnSetCenter();
                    break;
            }
        }
        else {
            fnSetCenter();
        }
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first) {
            switch (name) {
                case 'libraries':
                case 'apiKey':
                case 'language':
                case 'region':
                    if (this.#bootstrapped) {
                        this._bootstrapDebounced();
                    }
                    break;
                case 'defaultOptions':
                    for (let map of this.enrolledMaps()) {
                        this._updateMapOptions(map);
                    }
                    break;
                default:
                    for (let map of this.enrolledMaps()) {
                        this.invalidateSize(map);
                    }
                    break;
            }
        }
    }
    // #region ABSTRACT IMPLEMENTATION
    dispose(map) {
        const listeners = this.#listeners.get(map);
        while (listeners.length) {
            google.maps.event.removeListener(listeners.pop());
        }
        this.#listeners.delete(map);
    }
    _updateMapOptions(ctrl) {
        if (!this.hasMap(ctrl)) {
            return;
        }
        const { map } = this.tryGetMap(ctrl);
        map.setOptions(this._buildMapOptions(ctrl));
    }
    _buildMapOptions(ctrl) {
        const scale = ctrl.scale;
        const draggable = ctrl.draggable;
        const dblClickZoom = ctrl.doubleClickZoom;
        const kbShortcuts = ctrl.keyboardShortcuts;
        const { type: mapTypeId, theme: colorScheme } = getNativeMapStyle({ type: ctrl.type, theme: ctrl.theme });
        return Utils.extend({}, this.defaultOptions || { streetViewControl: false, zoomControl: false, mapTypeControl: false, fullscreenControl: false, cameraControl: false }, {
            zoomControl: scale && !Utils.isNullOrEmpty(ctrl.zoomControl),
            zoomControlOptions: {
                position: (function (zpos) {
                    switch (zpos) {
                        case 'topright':
                            return google.maps.ControlPosition.RIGHT_TOP;
                        case 'bottomleft':
                            return google.maps.ControlPosition.LEFT_BOTTOM;
                        case 'bottomright':
                            return google.maps.ControlPosition.RIGHT_BOTTOM;
                        default:
                            return google.maps.ControlPosition.LEFT_TOP;
                    }
                })(ctrl.zoomControl)
            },
            scrollwheel: ctrl.mousewheel,
            disableDoubleClickZoom: dblClickZoom,
            keyboardShortcuts: kbShortcuts,
            draggable: draggable,
            mapTypeId,
            colorScheme,
            tilt: ctrl.tilt,
            heading: ctrl.heading,
        });
    }
    #bootstrapped = false;
    _bootstrapDebounced() {
        this._bootstrap().then(_ => {
            for (let map of this.enrolledMaps()) {
                // HACK: (toggle adapter off/on)
                map.adapter = null;
                map.adapter = this;
            }
        });
    }
    _bootstrap() {
        return new Promise((resolve, reject) => {
            const apiKey = this.apiKey, language = this.language || '', region = this.region || '', version = this.version || 'weekly';
            bootstrapper.boostrap(apiKey, language, region, version).then(_ => {
                const promises = [];
                for (let lib of this.libraries || []) {
                    promises.push(google.maps.importLibrary(lib));
                }
                Promise.all(promises).then(_ => {
                    resolve();
                    this.#bootstrapped = true;
                }, reject);
            }, reject);
        });
    }
    async init(container) {
        await this._bootstrap();
        //
        const ctrl = container;
        const centerPos = MapUtils.parseCoords(ctrl.center);
        const center = new google.maps.LatLng(centerPos[0], centerPos[1]);
        const mapOptions = this._buildMapOptions(ctrl);
        const canvas = ctrl.container;
        const mapElement = document.createElement('div');
        mapElement.style.width = '100%';
        mapElement.style.height = '100%';
        canvas.innerHTML = '';
        canvas.appendChild(mapElement);
        const map = new google.maps.Map(mapElement, mapOptions);
        const listeners = [];
        this.#listeners.set(ctrl, listeners);
        MAP_EVENTS.forEach(evt => {
            listeners.push(map.addListener(evt, this._spinMapUpdateHandler(ctrl, map)));
        });
        // setting now the center and zoom, triggers the "load" event and activates the child-components, if any.
        map.setZoom(ctrl.zoom);
        map.setCenter(center);
        // first-load
        var listener = map.addListener('idle', function once(e) {
            listener.remove();
            container.dispatchEvent(new MapEvent("maploaded"));
        });
        // tilesloaded
        listeners.push(map.addListener('tilesloaded', evt => {
            container.dispatchEvent(new MapEvent("tilesloaded"));
        }));
        // call setView NOW to trigger the load event :(
        const cnt = MapUtils.parseCoords(ctrl.center), xpr = new google.maps.LatLng(cnt[0], cnt[1]);
        map.setZoom(ctrl.zoom);
        map.setCenter(xpr);
        return { map, container: mapElement };
    }
    invalidateSize(ctrl) {
        if (!this.hasMap(ctrl)) {
            return;
        }
        const { map } = this.tryGetMap(ctrl);
        google.maps.event.trigger(map, "resize");
    }
    //#endregion
    _spinMapUpdateHandler(ctrl, map) {
        return () => {
            if (!Utils.isNull(map) && !Utils.isNull(ctrl)) {
                const center = map.getCenter();
                if (Utils.isNull(center)) {
                    // not fully initialized yet
                    return;
                }
                this.updateMapElement(ctrl, {
                    center: { lat: center.lat(), lng: center.lng() }, zoom: map.getZoom(),
                    type: map.getMapTypeId(), tilt: map.getTilt(), heading: map.getHeading()
                });
            }
        };
    }
    fitBoundsNative(ctrl, bounds, padding = this._getPadding(ctrl)) {
        const map = this.tryGetMap(ctrl)?.map;
        if (Utils.isNull(map)) {
            return;
        }
        const bnds = GMapsUtils.getBounds(bounds);
        GMapsUtils.fitBounds(map, bnds, padding);
    }
    ;
    _getPadding(ctrl) {
        const top = ctrl.paddingTop || 0, left = ctrl.paddingLeft || 0, right = ctrl.paddingRight || 0, bottom = ctrl.paddingBottom || 0;
        if (left || top || right || bottom) {
            return { top, bottom, left, right };
        }
        return null;
    }
    /**
     @deprecated
     */
    _padBounds(ctrl) {
        const paddingTop = ctrl.paddingTop || 0, paddingLeft = ctrl.paddingLeft || 0, paddingRight = ctrl.paddingRight || 0, paddingBottom = ctrl.paddingBottom || 0;
        if (paddingLeft || paddingTop || paddingRight || paddingBottom) {
            const map = this.tryGetMap(ctrl).map;
            const bnds = map.getBounds();
            const w = map.getDiv().clientWidth, h = map.getDiv().clientHeight;
            //
            const n_nw = map.getProjection().fromPointToLatLng(new google.maps.Point(-paddingLeft, -paddingTop));
            const n_se = map.getProjection().fromPointToLatLng(new google.maps.Point(w + paddingRight, h + paddingBottom));
            //
            bnds.extend(n_nw);
            bnds.extend(n_se);
        }
    }
    _redrawMap() {
        // do nothing (no tiles)
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemGoogleMapAdapterElement.prototype, "apiKey", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemGoogleMapAdapterElement.prototype, "version", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemGoogleMapAdapterElement.prototype, "language", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemGoogleMapAdapterElement.prototype, "region", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemGoogleMapAdapterElement.prototype, "defaultOptions", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.StringArray })
], PacemGoogleMapAdapterElement.prototype, "libraries", void 0);
__decorate([
    Debounce(consts.DETACH_TIMEOUT)
], PacemGoogleMapAdapterElement.prototype, "_bootstrapDebounced", null);
PacemGoogleMapAdapterElement = __decorate([
    CustomElement({ tagName: P + '-map-adapter-google' })
], PacemGoogleMapAdapterElement);
export { PacemGoogleMapAdapterElement };
//#region geocoders
class PacemGoogleMapGeocoder {
    constructor(apiKey, language = '', region = '', version = 'weekly') {
        this.apiKey = apiKey;
        this.language = language;
        this.region = region;
        this.version = version;
    }
    /**
     * Try add altitude info.
     * @param results
     * @returns
     */
    _elevateResults(set) {
        return new Promise((resolve, reject) => {
            // try get elevation as well
            const elevation = new google.maps.ElevationService();
            elevation.getElevationForLocations({ locations: set.map(i => i.geometry.location) }, (results2, status2) => {
                if (status2 === google.maps.ElevationStatus.OK) {
                    const retval = GMapsUtils.mapGeocodeResults(set, results2);
                    // success
                    resolve(retval);
                }
                else {
                    // partial success (no elevation)
                    const retval = GMapsUtils.mapGeocodeResults(set);
                    resolve(retval);
                }
            });
        });
    }
    geocode(search, maxResults = 10) {
        return new Promise((resolve, reject) => {
            bootstrapper.boostrap(this.apiKey, this.language || '', this.region || '', this.version || 'weekly').then(_ => {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({
                    address: search,
                    //language: this.language || '',
                    //region: this.region || ''
                }, (results, status) => {
                    if (status === google.maps.GeocoderStatus.OK
                        || status == google.maps.GeocoderStatus.ZERO_RESULTS) {
                        const set = (results || [])
                            .filter((_, index) => index < (maxResults || 10));
                        this._elevateResults(set).then(resolve);
                    }
                    else {
                        // error
                        reject(status);
                    }
                });
            }, reject);
        });
    }
    _reverseGeocodeInternal(position, maxResults) {
        const location = GMapsUtils.getPosition(position);
        return new Promise((resolve, reject) => {
            bootstrapper.boostrap(this.apiKey, this.language || '', this.region || '', this.version || 'weekly').then(_ => {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({
                    location,
                    //language: this.language || '',
                    //region: this.region || ''
                }, (results, status) => {
                    if (status === google.maps.GeocoderStatus.OK
                        || status == google.maps.GeocoderStatus.ZERO_RESULTS) {
                        const set = (results || [])
                            .filter((_, index) => index < (maxResults || 10));
                        this._elevateResults(set).then(resolve);
                    }
                    else {
                        // error
                        reject(status);
                    }
                });
            }, reject);
        });
    }
    reverseGeocode(position, maxResults = 1) {
        return this._reverseGeocodeInternal(position, maxResults);
    }
}
//#endregion
//}
